/**
 * audioUnmute.js — module-level user-gesture capture.
 *
 * Why this file exists (READ before touching):
 * Browsers require a user activation gesture (click / touch / keydown) to
 * unmute an <audio> element that started with muted autoplay. React
 * components mount asynchronously; if the visitor clicks BEFORE
 * <MusicWidget /> mounts (which itself waits for /api/settings/public),
 * that first click is lost — and music stays muted forever.
 *
 * This module attaches the gesture listeners at IMPORT TIME (as soon as
 * `import './lib/audioUnmute'` runs at the top of index.js). That is much
 * earlier than any React render. When a gesture happens we:
 *   1. Set a module-level flag (`gestureSeen`)
 *   2. Notify every subscriber that registered via `onGesture(cb)`
 * MusicWidget calls `onGesture` on mount — its callback fires immediately
 * if a gesture already happened, otherwise on the next gesture.
 */

let gestureSeen = false;
const subscribers = new Set();
const EVENTS = ['touchstart', 'touchend', 'pointerup', 'click', 'keydown'];

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

const notifyAll = () => {
  // Copy so a subscriber that unsubscribes during iteration doesn't skip others.
  const snapshot = Array.from(subscribers);
  subscribers.clear();
  for (const cb of snapshot) {
    try { cb(); } catch { /* swallow */ }
  }
};

const handler = () => {
  if (gestureSeen) return;
  gestureSeen = true;
  // Detach the module-level listeners once — but keep flag set forever so
  // late subscribers still see it.
  EVENTS.forEach((ev) => document.removeEventListener(ev, handler, { capture: true }));
  notifyAll();
};

if (isBrowser) {
  // capture:true ensures we run even if a downstream handler calls
  // stopPropagation (e.g. a modal backdrop). passive:true lets scroll perf stay untouched.
  EVENTS.forEach((ev) => document.addEventListener(ev, handler, { capture: true, passive: true }));
}

/** Register a callback that runs on the first user gesture. If a gesture
 * already happened before you subscribed, the callback runs immediately
 * (on the current microtask). Returns an unsubscribe function. */
export const onGesture = (cb) => {
  if (typeof cb !== 'function') return () => {};
  if (gestureSeen) {
    // Fire synchronously — needed for iOS/Safari where the audio.play()
    // MUST happen inside the same activation window as the gesture.
    try { cb(); } catch { /* swallow */ }
    return () => {};
  }
  subscribers.add(cb);
  return () => subscribers.delete(cb);
};

/** True if any user gesture has fired since page load. */
export const wasGestureSeen = () => gestureSeen;

export default { onGesture, wasGestureSeen };
